import { useApps } from '@auth/useApps'
import AppPaths from '@globals/paths'
import { tabletQuery } from '@globals/themes/themeConstants'
import { useApiActionToast } from '@globals/toaster/useToasterStore'
import {
  AwardType,
  AwardTypeEmbeds,
  AwardTypeParams,
  MediaVariantEnum,
  createAwardType,
  editAwardType,
  getAwardType,
  initiateAwardUpload,
} from '@kudos/http-client'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useApiError } from '@shared/errors/useApiError'
import { useInvalidateQuery } from '@shared/hooks/useInvalidateQuery'
import useMediaUrl from '@shared/hooks/useMediaUrl'
import { AppPermission } from '@shared/permissions'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useT } from '@transifex/react'
import { FieldValues, UseFormSetError } from 'react-hook-form'
import { useNavigate } from 'react-router'
import {
  getArchived,
  getBudgetItemId,
  getCandidates,
  getDescription,
  getMaxNominations,
  getMediaObjectId,
  getName,
  getNomination,
  getNominationEndAt,
  getNominationStartAt,
  getNominators,
  getNotify,
  getPinForDays,
  getPoints,
  getPointsLocked,
  getQuestions,
  getSenders,
  getTenantWideCandidates,
  getTenantWideNominators,
} from './awardTypeParamHelpers'
import { AwardTypeFormData } from './useConfigureAwardTypeForm'

export const AWARD_TYPE_QK = 'awardTypes'
const useAwardTypeData = (
  awardTypeId?: string,
  embeds: AwardTypeEmbeds[] = [
    'attachmentUrls',
    'pointsBankName',
    'pointsBankBalance',
    'senders',
    'nominators',
    'candidates',
    'stats',
    'questions',
  ],
) => {
  const awardTypeQueryKey = [AWARD_TYPE_QK, awardTypeId]
  const isEdit = !!awardTypeId
  const isTablet = useMediaQuery(tabletQuery)
  const [showSuccess] = useApiActionToast()
  const { handleApiError } = useApiError()
  const invalidateQuery = useInvalidateQuery()
  const t = useT()
  const navigate = useNavigate()
  const [hasAppPermission] = useApps()
  const canConfigurePoints = hasAppPermission(
    'pointsApp',
    AppPermission.PointsAppViewPointsConfiguration,
  )

  const { data: awardTypeData, isFetched: isAwardTypeFetched } = useQuery({
    queryKey: awardTypeQueryKey,
    queryFn: () =>
      getAwardType(awardTypeId!, {
        embeds,
      }),
    enabled: !!awardTypeId,
  })

  const { mutateAsync: createOrSaveAwardTypeMutation } = useMutation({
    mutationFn: (params: AwardTypeParams) => {
      return isEdit ? editAwardType(awardTypeId, params) : createAwardType(params)
    },
    onSuccess: async (data: AwardType) => {
      showSuccess(isEdit ? t('Award successfully updated') : t('Award successfully created'))
      await invalidateQuery(awardTypeQueryKey)
      if (data.nomination) {
        navigate(AppPaths.tenant.admin.viewNominationAwardType(data.id))
      } else {
        navigate(AppPaths.tenant.admin.viewAwardType(data.id))
      }
    },
    onError: error => handleApiError(error),
  })

  const initiateMediaUploadMutation = useMutation({
    mutationFn: ({
      originalFilename,
      contentType,
    }: {
      originalFilename: string
      contentType: string
    }) => initiateAwardUpload({ originalFilename, contentType }),
  })

  const { url: awardTypeMediaUrl } = useMediaUrl(
    awardTypeData?.attachmentUrls,
    isTablet ? MediaVariantEnum.AWARD_BADGE_MOBILE : MediaVariantEnum.AWARD_BADGE_DESKTOP,
  )

  const createOrSaveAwardType = async (
    formData: AwardTypeFormData,
    isNomination: boolean,
    mediaObjectId: string | null,
    setError: UseFormSetError<FieldValues>,
    reset: () => void,
  ) => {
    const payload: AwardTypeParams = {
      name: getName(formData),
      description: getDescription(formData),
      points: getPoints(formData, canConfigurePoints),
      senders: getSenders(formData),
      pointsLocked: getPointsLocked(formData, canConfigurePoints),
      budgetItemId: getBudgetItemId(formData, canConfigurePoints),
      mediaObjectId: getMediaObjectId(formData, mediaObjectId),
      archived: getArchived(formData, isNomination),
      nomination: getNomination(formData),
      nominationStartAt: getNominationStartAt(formData, isNomination),
      nominationEndAt: getNominationEndAt(formData, isNomination),
      notify: getNotify(formData),
      pinForDays: getPinForDays(formData, isNomination),
      maxNominations: getMaxNominations(formData),
      tenantWideNominators: getTenantWideNominators(formData, isNomination),
      tenantWideCandidates: getTenantWideCandidates(formData, isNomination),
      candidates: getCandidates(formData, isNomination),
      nominators: getNominators(formData, isNomination),
      questions: getQuestions(formData, isNomination),
    }

    await createOrSaveAwardTypeMutation(payload, {
      onSuccess: () => {
        reset()
      },
      onError: error =>
        handleApiError(error, setError, [
          'name',
          'points',
          'pointsLocked',
          'senders',
          'description',
          'pointsLocked',
          'archived',
          'budgetItemId',
          'dateTimeRange',
          'candidates',
          'nominators',
        ]),
    })
  }

  return {
    awardTypeData,
    isAwardTypeFetched,
    awardTypeMediaUrl,
    createOrSaveAwardType,
    initiateMediaUploadMutation,
  }
}

export default useAwardTypeData
